import { useQuery, type ApolloError } from '@apollo/client';
import { useRef } from 'react';

import {
  GetPremiumContactsDocument,
  type GetPremiumContactsQuery,
} from '@xing-com/crate-upsell-graphql';
import type { RefinedPremiumContacts } from '@xing-com/crate-upsell-types';
import type { FacepileActorProps } from '@xing-com/facepile';

export function getRefinedPremiumContacts(
  data: GetPremiumContactsQuery
): FacepileActorProps[] | [] {
  if (!data) return [];

  const edges = data.viewer?.premiumContacts?.edges || [];

  return edges.map((edge) => {
    const xingId = edge?.node?.xingId;

    return {
      id: xingId?.id ?? '',
      profileName: `${
        xingId?.displayName ?? `${xingId?.firstName} ${xingId?.lastName}`
      }`,
      src: xingId?.profileImage?.[0]?.url ?? '',
    };
  });
}

export type UsePremiumContacts = {
  premiumContactsLoading: boolean;
  premiumContactsError: ApolloError | undefined;
  premiumContacts: RefinedPremiumContacts | undefined;
};

export const usePremiumContacts = (): UsePremiumContacts => {
  const premiumContactsRef = useRef<{
    total: number;
    contacts: FacepileActorProps[];
  }>({ total: 0, contacts: [] });

  const {
    loading: premiumContactsLoading,
    error: premiumContactsError,
    data,
  } = useQuery(GetPremiumContactsDocument, {
    variables: { first: 4 },
    fetchPolicy: 'cache-first',
  });

  if (data?.viewer?.premiumContacts) {
    premiumContactsRef.current = {
      contacts: getRefinedPremiumContacts(data),
      total: data.viewer.premiumContacts.total,
    };
  }

  return {
    premiumContactsLoading,
    premiumContactsError,
    premiumContacts: premiumContactsRef.current,
  };
};
